"use client"

import { useState, useEffect, useMemo } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { useApp } from "../contexts/AppContext"
import { BiX, BiArrowBack, BiFile, BiCog, BiFolder, BiSave, BiEdit, BiCloudUpload, BiExport, BiRefresh } from "react-icons/bi"
import { motion } from "framer-motion"

import AnimatedLoader from "../components/ui/AnimatedLoader"
import HeaderAlert from "../components/ui/HeaderAlert"
import DataInputForm from "../components/forms/DataInputForm"
import DataImportForm from "../components/forms/DataImportForm"
import FeuilleDataTable from "../components/tables/FeuilleDataTable"

export default function FeuilleDataPage() {
  const { feuilleId, dataId } = useParams()
  const navigate = useNavigate()
  const {
    getFeuille,
    getFeuilleLignes,
    getFeuilleDataAPI,
    addFeuilleDataAPI,
    deleteFeuilleDataAPI,
    loading,
    error,
    success,
    clearError,
    clearSuccess,
  } = useApp()

  const [dataInput, setDataInput] = useState([])
  const [dataEdit, setDataEdit] = useState([])
  const [feuilleData, setFeuilleData] = useState([])
  const [mode, setMode] = useState('insert')

  const projetId = localStorage.getItem("selec-projet")
  const classeurId = localStorage.getItem("selec-classeur")
  const feuille = useMemo(() => { return getFeuille(feuilleId) }, [feuilleId, getFeuille])
  const columns = getFeuilleLignes(feuilleId) || []
  const visibleChamps = useMemo(() => columns.filter((c) => c.afficher.toLowerCase() === "oui"), [columns])

  useEffect(() => {
    if (feuilleId && feuille !== null) {
      getFeuilleDataAPI(feuilleId)
        .then(data => {
          if (data.length > 0) {
            setFeuilleData(dataId ? data.filter((data) => data.id != dataId) : data)
            setDataEdit(dataId ? data.find((data) => data.id == dataId) : null)
          }
        })
        .catch(error => /* TODO */ console.error("Erreur lors du chargement des données:", error))
    }
  }, [feuilleId, dataId])

  const handleSaveData = async (e) => {
    e.preventDefault()

    const dataInputFormated = dataInput.map((data) => {
      if (data.key_data) delete data.key_data
      return { "code_feuille": Number.parseInt(feuilleId), "Lignes": data }
    })

    if (dataInput.length > 0 && dataId) {
      const dataDelete = { "code_feuille": Number.parseInt(feuilleId), "ids": [dataId] }
      deleteFeuilleDataAPI(dataDelete)
    }

    await Promise.all(dataInputFormated.map((data) => addFeuilleDataAPI(data)))
    setDataInput([])
    navigate(`/classeur/${feuille.code_classeur}`)
  }

  const handleExportCanevas = () => {
    const headers = visibleChamps.map((c) => c.nom_ligne)
    const csvContent = [
      headers.join(';'),
      headers.map(() => '').join(';')
    ].join('\n')

    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', `canevas_${feuille.nom_feuille || 'feuille'}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const headerVariants = {
    hidden: { y: -50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
  }

  if (!feuille) {
    return (
      <div className="m-3 p-3 border shadow-sm rounded-1 bg-white" style={{ minHeight: "95vh" }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-5"
        >
          <BiFile className="display-1 text-danger mb-3" />
          <h3 className="text-danger">Feuille non trouvée</h3>
          <p className="text-muted mb-4">La feuille demandée n'existe pas ou a été supprimée.</p>
          <button onClick={() => navigate(`/classeur/${classeurId}`)} className="btn btn-primary">
            <BiArrowBack className="me-1" />
            Retour au classeur
          </button>
        </motion.div>
      </div>
    )
  }

  if (loading && columns.length === 0) {
    return (
      <div className="m-3 p-3 border shadow-sm rounded-1 bg-white" style={{ minHeight: "95vh" }}>
        <AnimatedLoader text="Chargement des columns..." />
      </div>
    )
  }

  if (columns.length === 0) {
    return (
      <div className="m-3 p-3 border shadow-sm rounded-1 bg-white" style={{ minHeight: "95vh" }}>
        {/* En-tête */}
        <motion.div variants={headerVariants} initial="hidden" animate="visible" className="mb-4">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div className="flex-grow-1">
              <nav aria-label="breadcrumb">
                <motion.ol
                  className="breadcrumb mb-2"
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.1 }}>
                  <li className="breadcrumb-item">
                    <Link to={`/projet/${projetId}`} className="text-decoration-none d-flex align-items-center">
                      <BiFolder className="me-1" /> Classeur
                    </Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={`/classeur/${feuille.code_classeur}`} className="text-decoration-none d-flex align-items-center">
                      <BiFile className="me-1" /> Feuille
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">Insertion de données</li>
                </motion.ol>
              </nav>
              <h5 className="mb-0">Insertion de données - {feuille.nom_feuille}</h5>
            </div>

            <div className="d-flex align-items-center justify-content-end gap-2">
              <motion.button
                onClick={() => window.location.reload()}
                title="Rafraichir"
                className="btn btn-sm btn-outline-secondary d-flex align-items-center p-1 rounded-circle"
                disabled={loading}>
                <BiRefresh className="fs-5" />
              </motion.button>

              <motion.button className="btn btn-sm btn-success px-2 d-flex align-items-center"
                onClick={handleSaveData}
                disabled={loading}>
                <BiSave className="me-1" />
                Sauvegarder
              </motion.button>

              <motion.button className="btn btn-sm btn-secondary px-2 d-flex align-items-center"
                onClick={() => navigate(`/classeur/${feuille.code_classeur}`)}
                disabled={loading}>
                <BiX className="me-1" /> Annuler
              </motion.button>
            </div>
          </div>
        </motion.div>

        <div className="card text-center py-5">
          <p className="text-muted">
            Aucun champ défini pour cette feuille. <br />
            Veuillez configurer les champs avant d'ajouter des données.
          </p>
          <Link to={`/feuille/${feuilleId}/fields`}
            className="btn btn-primary d-flex align-items-center mx-auto" >
            <BiCog className="me-1" /> Configurer les champs
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="m-3 p-3 border shadow-sm rounded-1 bg-white" style={{ minHeight: "95vh" }}>
      <HeaderAlert error={error} success={success} clearError={clearError} clearSuccess={clearSuccess} />

      {/* En-tête */}
      <motion.div variants={headerVariants} initial="hidden" animate="visible" className="mb-4">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="flex-grow-1">
            <nav aria-label="breadcrumb">
              <motion.ol
                className="breadcrumb mb-2"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}>
                <li className="breadcrumb-item">
                  <Link to={`/projet/${projetId}`} className="text-decoration-none d-flex align-items-center">
                    <BiFolder className="me-1" /> Classeur
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={`/classeur/${feuille.code_classeur}`} className="text-decoration-none d-flex align-items-center">
                    <BiFile className="me-1" /> Feuille
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">Insertion de données</li>
              </motion.ol>
            </nav>
            <h5 className="mb-0">Insertion de données - {feuille.nom_feuille}</h5>
          </div>

          <div className="d-flex gap-2">
            <motion.button
              onClick={() => window.location.reload()}
              title="Rafraichir"
              className="btn btn-sm btn-outline-secondary d-flex align-items-center p-1 rounded-circle"
              disabled={loading}>
              <BiRefresh className="fs-5" />
            </motion.button>

            <motion.button className="btn btn-sm btn-success px-2 d-flex align-items-center"
              onClick={handleSaveData}
              disabled={loading}>
              <BiSave className="me-1" />
              Sauvegarder
            </motion.button>

            <motion.button className="btn btn-sm btn-secondary px-2 d-flex align-items-center"
              onClick={() => navigate(`/classeur/${feuille.code_classeur}`)}
              disabled={loading}>
              <BiX className="me-1" /> Annuler
            </motion.button>
          </div>
        </div>
      </motion.div>

      <div className="row">
        <div className="col-md-8">
          {loading && feuilleData.length === 0 ? (
            <AnimatedLoader text="Chargement des champs..." />
          ) : (
            <motion.div
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="card rounded-1 shadow-sm h-100" style={{ minHeight: "500px" }}>
              <div className="card-header bg-light d-flex justify-content-between align-items-center py-1">
                <h6 className="mb-0">Données existantes</h6>

                <div className="d-flex gap-2">
                  <button className="btn btn-sm btn-light px-2 d-flex align-items-center"
                    onClick={handleExportCanevas}
                    disabled={loading}>
                    <BiExport className="me-1" />
                    Exporter Canevas
                  </button>
                </div>
              </div>
              <div className="card-body px-3 py-0">
                <FeuilleDataTable
                  feuilleId={feuilleId}
                  classeurId={classeurId}
                  feuilleData={[...feuilleData, ...dataInput]}
                  dataInput={dataInput}
                  setDataInput={setDataInput}
                  loading={loading} />
              </div>
            </motion.div>
          )}
        </div>

        <div className="col-md-4">
          <motion.div
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className="card rounded-1 shadow-sm">
            <div className="card-header bg-light d-flex justify-content-between align-items-center py-1">
              <h6 className="mb-0">Ajouter des données</h6>

              <div className="btn-group">
                {/* button pour inserer */}
                <button className={`${mode === 'insert' ? 'active' : ''} d-flex align-items-center btn btn-sm btn-light`}
                  onClick={() => setMode('insert')}>
                  <BiEdit className="me-1 fs-6" />Saisir
                </button>
                {/* button pour importer */}
                <button className={`${mode === 'import' ? 'active' : ''} d-flex align-items-center btn btn-sm btn-light`}
                  onClick={() => setMode('import')}>
                  <BiCloudUpload className="me-1 fs-6" />Importer
                </button>
              </div>
            </div>

            <div className="card-body">
              {mode === 'insert' ? (
                <DataInputForm
                  visibleChamps={visibleChamps}
                  dataInput={dataInput}
                  setDataInput={setDataInput}
                  dataEdit={dataEdit}
                  setDataEdit={setDataEdit}
                  loading={loading} />
              ) : (
                <DataImportForm
                  visibleChamps={visibleChamps}
                  setDataInput={setDataInput}
                  loading={loading} />
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
